// v641.2: Patch Notes modal. Auto-shows on first login of a new
// version (compares LATEST_VERSION against localStorage), and is also
// re-openable from the Settings panel.
import React, { useEffect, useMemo } from 'react';
import { PATCH_NOTES, LATEST_VERSION } from '../data/patchNotes';
import '../styles/Social.css';

const STORAGE_KEY = 'loria_last_patch_seen';

export const getLastSeenVersion = () => {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
};

export const markPatchNotesSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, LATEST_VERSION); } catch { /* ignore */ }
};

const PatchNotesModal = ({ onClose, sinceVersion = null }) => {
  // Show all entries newer than `sinceVersion` (string compare on version order).
  // Since PATCH_NOTES is newest-first, find the index where version === sinceVersion
  // and show everything before it. Falls back to all entries if no prior version.
  const entries = useMemo(() => {
    if (!sinceVersion) return PATCH_NOTES;
    const idx = PATCH_NOTES.findIndex(e => e.version === sinceVersion);
    if (idx === -1) return PATCH_NOTES;
    return PATCH_NOTES.slice(0, idx);
  }, [sinceVersion]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // If nothing is new since the player's last seen version, auto-dismiss.
  useEffect(() => {
    if (entries.length === 0) {
      try { localStorage.setItem(STORAGE_KEY, LATEST_VERSION); } catch { /* ignore */ }
      if (onClose) onClose();
    }
  }, [entries.length, onClose]);

  const handleClose = () => {
    markPatchNotesSeen();
    if (onClose) onClose();
  };

  if (entries.length === 0) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 600, padding: 18, maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#c9a84c', fontSize: 20 }}>📰 What's New</h2>
          <button
            onClick={handleClose}
            style={{ background: 'transparent', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            title="Close (Esc)"
          >×</button>
        </div>

        <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
          {sinceVersion
            ? `Updates since ${sinceVersion} — welcome back!`
            : `Welcome to Loria! Here's what's new in the latest releases.`}
        </div>

        {entries.map(entry => (
          <div key={entry.version} style={{ marginBottom: 18 }}>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              borderBottom: '1px solid #c9a84c33',
              paddingBottom: 4,
              marginBottom: 8,
            }}>
              <span style={{ color: '#c9a84c', fontSize: 15, fontWeight: 600 }}>{entry.version}</span>
              <span style={{ color: '#dcd0a0', fontSize: 13 }}>{entry.title}</span>
              {entry.date && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#666' }}>{entry.date}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {entry.highlights.map((h, i) => (
                <div key={i} style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  background: 'rgba(20, 20, 28, 0.5)',
                  padding: '6px 10px',
                  borderRadius: 4,
                  borderLeft: '2px solid #c9a84c66',
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{h.icon || '•'}</span>
                  <span style={{ fontSize: 12, color: '#dcd0a0', lineHeight: 1.4 }}>{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
          <button
            onClick={handleClose}
            style={{
              background: 'linear-gradient(180deg, #c9a84c, #a08b3a)',
              color: '#1a1a22',
              border: 'none',
              padding: '8px 24px',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default PatchNotesModal;
